import React, {useEffect, useState} from 'react';
import { BrowserRouter as Router } from 'react-router-dom';
import constants from 'util/constants';

import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  ArcElement,
  Title,
  Tooltip,
  Legend,
} from 'chart.js';
import { ThemeProvider } from '@mui/material/styles';
import { ThemeProvider as StyledThemeProvider } from '@mui/styles';
import validate from 'validate.js';

import theme from './theme';
import './assets/scss/index.scss';
import validators from './common/validators';



import { Provider } from 'react-redux';
import { store } from './store';

import Routes from './Routes.jsx';
// Firebase is initialized in util/firebase.js
import { MessageSnackbars } from 'components';
import { ConfirmProvider } from 'material-ui-confirm';
import {
  QueryClient,
  QueryClientProvider,
} from "@tanstack/react-query";

import analytics from './common/analytics.js'
import ErrorBoundary from './ErrorBoundary.jsx'

// Register Chart.js components
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  ArcElement,
  Title,
  Tooltip,
  Legend
);



// Firebase is initialized in util/firebase.js


const cachedVersionKey = "cachedVersion"

/* Attach listeners once */
analytics.on('page', () => {
  console.log('Page')
})


const newQueryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 2,
      refetchOnWindowFocus: false,
      staleTime: 5 * 60 * 1000, // 5 minutes
      gcTime: 10 * 60 * 1000, // 10 minutes
      networkMode: 'offlineFirst',
    },
  },
});

// Chart.js v4 uses different configuration approach
// Custom chart configurations should be handled in component level

validate.validators = {
  ...validate.validators,
  ...validators
};

function isCrawler(userAgent) {
  const bots = [
    'bot',
    'crawler',
    'spider',
    'search',
    'googlebot',
    'bingbot',
    'yandexbot'
  ];

  for (let i = 0; i < bots.length; i++) {
    if (userAgent.indexOf(bots[i]) > -1) {
      return true;
    }
  }

  return false;
}

const App = () => {
  // console.log('App component initializing');

  const [isBot, setIsBot] = useState(false);
  const cachedVersion = localStorage.getItem(cachedVersionKey);

  // Suppress known third-party library warnings and errors
  useEffect(() => {
    const originalWarn = console.warn;
    const originalError = console.error;
    
    console.warn = (...args) => {
      const message = args[0]?.toString() || '';
      
      // Suppress @emotion/react duplicate loading warning
      if (message.includes('@emotion/react') && message.includes('already loaded')) {
        return;
      }
      originalWarn.apply(console, args);
    };

    console.error = (...args) => {
      const message = args[0]?.toString() || '';
      
      // Suppress react-scroll-section setState during render warning
      if (
        (message.includes('Cannot update a component') || message.includes('Warning: Cannot update')) &&
        message.includes('while rendering a different component')
      ) {
        return;
      }
      
      // Also suppress if it mentions react-scroll-section specifically
      if (message.includes('react-scroll-section') && message.includes('setState')) {
        return;
      }
      
      originalError.apply(console, args);
    };

    return () => {
      console.warn = originalWarn;
      console.error = originalError;
    };
  }, []);

  useEffect(() => {
    const userAgent = navigator.userAgent.toLowerCase();

    if (isCrawler(userAgent)) {
      setIsBot(true);
    }
    // Only check version if both values exist and are different
    if (cachedVersion && constants.WEBSITE_VERSION && String(constants.WEBSITE_VERSION) !== String(cachedVersion)) {
      localStorage.setItem(cachedVersionKey, constants.WEBSITE_VERSION);
      window.location.reload();
    }
    // Set version in localStorage
    if (constants.WEBSITE_VERSION) {
      localStorage.setItem(cachedVersionKey, constants.WEBSITE_VERSION);
    }
  }, [cachedVersion]);

  if (isBot) {
    return null;
  }

  return (
    <ErrorBoundary>
      <QueryClientProvider client={newQueryClient}>
        <ThemeProvider theme={theme}>
          <StyledThemeProvider theme={theme}>
            <Provider store={store}>
              <MessageSnackbars />
              <ConfirmProvider>
                <Router>
                  <Routes />
                </Router>
              </ConfirmProvider>
            </Provider>
          </StyledThemeProvider>
        </ThemeProvider>
      </QueryClientProvider>
    </ErrorBoundary>
  );
}

export default App;
